import { ApolloCache, Reference } from '@apollo/client'
import { DebitCardAccountType } from '@npco/mp-gql-types'
import {
  ListItemAccountsDebitCardAccountV2Fragment,
  ListItemAccountsDebitCardAccountV2FragmentDoc,
} from 'features/Accounts/Accounts/ListAccounts/ListItemAccounts/ListItemAccounts.generated'

import { AccountUpdateFragment } from './graphql/AccountUpdateFragment.generated'

type ListPosition = 'start-of-list' | 'end-of-list'

const writeFragment = (
  cache: ApolloCache<any>,
  newAccount: ListItemAccountsDebitCardAccountV2Fragment
) => {
  cache.writeFragment<ListItemAccountsDebitCardAccountV2Fragment>({
    fragment: ListItemAccountsDebitCardAccountV2FragmentDoc,
    fragmentName: 'ListItemAccountsDebitCardAccountV2Fragment',
    id: cache.identify({
      __typename: 'DebitCardAccountV2',
      id: newAccount.id,
    }),
    data: newAccount,
  })
}

export const cacheNewAccount = (
  cache: ApolloCache<any>,
  accountUpdate: AccountUpdateFragment,
  position: ListPosition = 'end-of-list'
) => {
  const newAccount: ListItemAccountsDebitCardAccountV2Fragment = {
    ...accountUpdate,
    __typename: 'DebitCardAccountV2' as const,
    cardsCount: {
      debit: 0,
      total: 0,
      expense: 0,
    },
  }

  cache.modify({
    fields: {
      getDebitCardAccountsV2: (
        existingAccountsRefs: { accounts: Reference[] },
        { toReference, readField }
      ) => {
        if (
          newAccount.type !== DebitCardAccountType.ZLR_DEBIT ||
          existingAccountsRefs.accounts.some(
            (cachedAccountRef: Reference) =>
              readField('id', cachedAccountRef) === accountUpdate.id
          )
        ) {
          return existingAccountsRefs
        }

        writeFragment(cache, newAccount)
        return {
          ...existingAccountsRefs,
          accounts:
            position === 'start-of-list'
              ? [toReference(newAccount), ...existingAccountsRefs.accounts]
              : [...existingAccountsRefs.accounts, toReference(newAccount)],
        }
      },
      getSavingsAccounts: (
        existingAccountsRefs: Reference[],
        { toReference, readField }
      ) => {
        if (
          newAccount.type !== DebitCardAccountType.SAVINGS ||
          existingAccountsRefs.some(
            (existingAccountsRef) =>
              readField('id', existingAccountsRef) === accountUpdate.id
          )
        ) {
          return existingAccountsRefs
        }

        writeFragment(cache, newAccount)
        return position === 'start-of-list'
          ? [toReference(newAccount), ...existingAccountsRefs]
          : [...existingAccountsRefs, toReference(newAccount)]
      },
    },
  })
}
